import styles from "@/styles/Navbar.module.css";
import Link from "next/link";
import { ShoppingCart, X } from "lucide-react";
import { useRouter } from "next/router";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Castle, Darts, Database, Game, Other, Tent } from "./Database";
import BasketCell from "./BasketCell";

export default function Navbar() {
  const [database, setDatabase] = useState<Database | null>(null);
  const router = useRouter();
  const nl = router.asPath.includes("/nl/");
  const fr = router.asPath.includes("/fr/");
  const cookies = router.asPath.includes("/cookies");
  const [basketVisible, setBasketVisible] = useState(false);
  const [basketEmpty, setBasketEmpty] = useState(true);
  const [basketContent, setbasketContent] = useState<ReactNode[]>([]);
  const [firstChoice, setFirstChoice] = useState<string | null>(null);
  const [secondChoice, setSecondChoice] = useState<string | null>(null);
  const [requestsUnformatted, setRequestsUnformatted] = useState<string | null>(null);
  const [multipleCastlesUnformatted, setMultipleCastlesUnformatted] = useState<string | null>(null);
  const [priceRequests, setPriceRequests] = useState<Other[] | null>([]);
  const [allTentRequests, setAllTentRequests] = useState<Tent[] | null>([]);
  const [allDartsRequests, setAllDartsRequests] = useState<Darts[] | null>([]);
  const [multipleCastles, setMultipleCastles] = useState<Castle[] | null>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [promotionVariable, setPromotionVariable] = useState<boolean | null>(null);

  const basketRef = useRef<HTMLDivElement>(null);
  const basketButtonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  const menuCartRef = useRef<HTMLButtonElement>(null);
  const fullMenuRef = useRef<HTMLDivElement>(null);

  const emptyBasketContent = (
    <div className={styles.basketEmpty}>
      {nl && <p>Uw winkelmandje is leeg.</p>}
      {fr && <p>Votre panier est vide.</p>}
    </div>
  );

  const showChoice = useCallback(
    (name: string | null, choiceString: string, errorString: string) => {
      if (name) {
        const castle = database
          ? database.castles.filter((castle) => castle.nameNL === name)[0]
          : null;
        const game = database ? database.games.filter((game) => game.nameNL === name)[0] : null;
        if (castle) {
          return (
            <div key={castle.nameFR}>
              <p className={styles.choice}>{choiceString}</p>
              <BasketCell
                type="basket"
                content={castle as Castle}
                contentType="castle"
                database={database ? database : ({} as Database)}
                promotionVariable={promotionVariable}
              />
            </div>
          );
        } else if (game) {
          return (
            <div key={game.nameFR}>
              <p className={styles.choice}>{choiceString}</p>
              <BasketCell
                type="basket"
                content={game as Game}
                contentType="game"
                database={database ? database : ({} as Database)}
                promotionVariable={promotionVariable}
              />
            </div>
          );
        }
      }
      if (
        (firstChoice == null && secondChoice != null) ||
        (firstChoice != null && secondChoice == null)
      ) {
        return (
          <p key={"basketError"} className={styles.choiceError}>
            {errorString}
          </p>
        );
      }
    },
    [firstChoice, secondChoice, database, promotionVariable]
  );

  const updateBasketContent = useCallback(() => {
    if (nl) {
      setbasketContent([
        showChoice(firstChoice, "Eerste keuze:", "Kies een springkasteel als eerste keuze, zodat we een alternatief kunnen voorstellen als het tweede niet beschikbaar is."),
        showChoice(secondChoice, "Tweede keuze:", "Kies een springkasteel als tweede keuze, zodat we een alternatief kunnen voorstellen als het eerste niet beschikbaar is."),
      ]);
    } else if (fr) {
      setbasketContent([
        showChoice(
          firstChoice,
          "Premier choix:",
          "Choisissez un château gonflable comme premier choix, afin que nous puissions proposer une alternative si le deuxième n'est pas disponible."
        ),
        showChoice(
          secondChoice,
          "Deuxième choix:",
          "Choisissez un château gonflable comme deuxième choix, afin que nous puissions proposer une alternative si le premier n'est pas disponible."
        ),
      ]);
    }
  }, [firstChoice, secondChoice, nl, fr, showChoice]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newFirstValue = sessionStorage.getItem("firstChoiceNL");
      const newSecondValue = sessionStorage.getItem("secondChoiceNL");
      const newRequestsUnformatted = sessionStorage.getItem("Requests");
      const newMultipleCastlesUnformatted = sessionStorage.getItem("multipleCastles");
      const newDatabase = sessionStorage.getItem("bumpfunDB");
      const newPromotionVariable = sessionStorage.getItem("specialPromo");
      if (newDatabase && newDatabase !== JSON.stringify(database)) {
        setDatabase(JSON.parse(newDatabase));
      }
      if (newPromotionVariable && (newPromotionVariable === "TRUE") !== promotionVariable) {
        setPromotionVariable(newPromotionVariable === "TRUE");
      }
      if (newFirstValue !== firstChoice) {
        setFirstChoice(newFirstValue);
        (router.asPath.includes("/verhuur") || router.asPath.includes("/location")) &&
          showBasketList();
      }
      if (newSecondValue !== secondChoice) {
        setSecondChoice(newSecondValue);
        (router.asPath.includes("/verhuur") || router.asPath.includes("/location")) &&
          showBasketList();
      }
      if (newRequestsUnformatted !== requestsUnformatted) {
        database && setRequestsUnformatted(newRequestsUnformatted);
        const newRequests = newRequestsUnformatted
          ? newRequestsUnformatted.split(",").concat()
          : null;
        const newPriceRequests =
          newRequests && database
            ? database.other.filter((other) => newRequests.includes(other.nameNL))
            : [];
        setPriceRequests(newPriceRequests as Other[]);
        const allTents = database ? database.tent : [];
        const newTentRequests =
          newRequests && database
            ? allTents.filter((tent) => newRequests.includes(tent.nameNL))
            : [];
        setAllTentRequests(newTentRequests as Tent[]);
        const allDarts = database ? database.darts : [];
        const newDartRequests =
          newRequests && database
            ? allDarts.filter((darts) => newRequests.includes(darts.nameNL))
            : [];
        setAllDartsRequests(newDartRequests as Darts[]);
        (router.asPath.includes("/verhuur") || router.asPath.includes("/location")) &&
          showBasketList();
      }
      if (newMultipleCastlesUnformatted !== multipleCastlesUnformatted) {
        database && setMultipleCastlesUnformatted(newMultipleCastlesUnformatted);
        const combined = database ? [...database.castles, ...database.games] : [];
        const newMultipleCastles = newMultipleCastlesUnformatted && database
          ? newMultipleCastlesUnformatted.split(",").concat().map((name) => combined.filter((castle) => castle.nameNL === name)[0])
          : null;
        setMultipleCastles(newMultipleCastles);
        (router.asPath.includes("/verhuur") || router.asPath.includes("/location")) &&
          showBasketList();
      }
    }, 100);
    if (firstChoice === null && secondChoice === null) {
      setbasketContent([]);
    } else {
      updateBasketContent();
    }
    return () => clearInterval(intervalId);
  }, [
    firstChoice,
    secondChoice,
    requestsUnformatted,
    multipleCastlesUnformatted,
    database,
    updateBasketContent,
    router.asPath,
    promotionVariable,
  ]);

  useEffect(() => {
    if (basketContent.length != 0 || (priceRequests && priceRequests.length != 0) || (allTentRequests && allTentRequests.length != 0) || (allDartsRequests && allDartsRequests.length != 0) || (multipleCastles && multipleCastles.length != 0)) {
      setBasketEmpty(false);
    } else {
      setBasketEmpty(true);
    }
  }, [basketContent, priceRequests, allTentRequests, allDartsRequests, multipleCastles]);

  function getURL() {
    const pathMap: Record<string, string> = {
      "/nl/verhuur": "/fr/location",
      "/nl/verkoop": "/fr/vente",
      "/nl/algemene-voorwaarden": "/fr/conditions",
      "/nl/reserveren": "/fr/reserver",
      "/nl/succesvol": "/fr/reussi",
      "/nl/verhuur-meerdere": "/fr/location-plusieurs",
      "/fr/location": "/nl/verhuur",
      "/fr/vente": "/nl/verkoop",
      "/fr/conditions": "/nl/algemene-voorwaarden",
      "/fr/reserver": "/nl/reserveren",
      "/fr/reussi": "/nl/succesvol",
      "/fr/location-plusieurs": "/nl/verhuur-meerdere",
    };

    return pathMap[router.pathname] || "/";
  }

  function showMultipleCastles() {
    return multipleCastles && database
      ? multipleCastles.map((castle) => (
          <BasketCell
            key={castle.nameFR}
            type="basket"
            content={castle as Castle}
            contentType="castle"
            database={database ? database : ({} as Database)}
            promotionVariable={promotionVariable}
          />
        ))
      : null;
  }

  function showPriceRequests() {
    return priceRequests
      ? priceRequests.map((other) => (
          <BasketCell
            key={other.imagePath}
            type="basket"
            content={other}
            contentType="other"
            database={database ? database : ({} as Database)}
            promotionVariable={promotionVariable}
          />
        ))
      : null;
  }

  function showTentRequests() {
    return allTentRequests
      ? allTentRequests.map((tent) => (
          <BasketCell
            key={tent.imagePath}
            type="basket"
            content={tent}
            contentType="tent"
            database={database ? database : ({} as Database)}
            promotionVariable={promotionVariable}
          />
        ))
      : null;
  }

  function showDartsRequests() {
    return allDartsRequests
      ? allDartsRequests.map((darts) => (
          <BasketCell
            key={darts.imagePath}
            type="basket"
            content={darts}
            contentType="darts"
            database={database ? database : ({} as Database)}
            promotionVariable={promotionVariable}
          />
        ))
      : null;
  }

  function toggleBasketList() {
    setBasketVisible((val) => !val);
    const basketBox = document.getElementsByClassName(
      styles.basketBox
    ) as HTMLCollectionOf<HTMLElement>;
    if (basketVisible) {
      basketBox[0].style.display = "none";
    } else {
      basketBox[0].style.display = "flex";
    }
  }

  function showBasketList() {
    setBasketVisible(true);
    const basketBox = document.getElementsByClassName(
      styles.basketBox
    ) as HTMLCollectionOf<HTMLElement>;
    basketBox[0].style.display = "flex";
  }

  const closeBasket = useCallback(() => {
    const basketBox = document.getElementsByClassName(
      styles.basketBox
    ) as HTMLCollectionOf<HTMLElement>;
    setBasketVisible(false);
    if (!cookies) {
      basketBox[0].style.display = "none";
    }
  }, [cookies]);

  // Check clicked outside of basket, close basket
  useEffect(() => {
    const handleOutSideClick = (event: Event) => {
      if (
        event.target != null &&
        !basketButtonRef.current?.contains(event.target as Node) &&
        !basketRef.current?.contains(event.target as Node)
      ) {
        closeBasket();
      }
    };
    window.addEventListener("mousedown", handleOutSideClick);
    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [closeBasket, basketButtonRef, basketRef]);

  useEffect(() => {
    const handleOutSideClick = (event: Event) => {
      if (
        event.target != null &&
        !menuCartRef.current?.contains(event.target as Node) &&
        !menuRef.current?.contains(event.target as Node) &&
        !fullMenuRef.current?.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", handleOutSideClick);
    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [menuCartRef, menuRef, fullMenuRef]);

  function checkRequests(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    const requests = sessionStorage.getItem("Requests");
    const tentRequests = database ? database.tent.map((tent) => tent.nameNL) : [];
    const dartsRequests = database ? database.darts.map((darts) => darts.nameNL) : [];
    const allTents = [...tentRequests, ...dartsRequests];
    if (requests) {
      const requestsArray = requests.split(",");
      requestsArray.forEach((request) => {
        const requestAmount = sessionStorage.getItem(request);
        if (requestAmount === "") {
          sessionStorage.setItem(request, "1");
        }
      });
      allTents.forEach((tent) => {
        if (sessionStorage.getItem(tent) && !requests.includes(tent)) {
          sessionStorage.removeItem(tent);
        }
      });
    } else {
      allTents.forEach((tent) => {
        sessionStorage.removeItem(tent);
      });
    }
  }

  return (
    <>
      <div className={styles.nav}>
        <Link className={styles.homeButton} href="/">
          <img
            className={styles.logo}
            src="/bumpfun-logo.png"
            alt="Bumpfun logo"
            width={120}
            height={51}
          />
        </Link>
        <div
          className={menuOpen ? [styles.menu, styles.menuOpen].join(" ") : styles.menu}
          ref={fullMenuRef}
        >
          <div className={styles.links}>
            <ul className={styles.redirects}>
              {nl && (
                <>
                  <li>
                    <Link className={styles.buy} href="/nl/verhuur">
                      Aanbod verhuur
                    </Link>
                  </li>
                  <li>
                    <Link className={styles.buy} href="/nl/verkoop">
                      Aanbod verkoop
                    </Link>
                  </li>
                  <li>
                    <Link className={styles.agreement} href="/nl/algemene-voorwaarden">
                      Algemene Voorwaarden
                    </Link>
                  </li>
                </>
              )}
              {fr && (
                <>
                  <li>
                    <Link className={styles.rent} href="/fr/location">
                      Location
                    </Link>
                  </li>
                  <li>
                    <Link className={styles.buy} href="/fr/vente">
                      Vente
                    </Link>
                  </li>
                  <li>
                    <Link className={styles.agreement} href="/fr/conditions">
                      Condition Générales
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
          {!cookies && (
            <div className={styles.languageAndCart}>
              <Link className={styles.languageNL} href={getURL()}>
                {fr && <>NL</>}
                {nl && <>FR</>}
              </Link>
              <div className={styles.basket} ref={basketButtonRef}>
                <button
                  className={styles.shoppingCart}
                  aria-label="Shoppingcart basket"
                  onClick={() => toggleBasketList()}
                >
                  {!basketEmpty && <div className={styles.bolleke}></div>}
                  {basketEmpty && <ShoppingCart />}
                  {!basketEmpty && (
                    <ShoppingCart width="30px" height="30px" color="#4D7EA8" fill="#4D7EA8" />
                  )}
                </button>
              </div>
              <div className={styles.basketBox} ref={basketRef}>
                <button
                  id="closeBasket"
                  className={styles.closeBasket}
                  onClick={() => closeBasket()}
                >
                  <X />
                </button>
                <div className={styles.basketContent}>
                  {!basketEmpty && (
                    <>
                      {basketContent}
                      {showMultipleCastles()}
                      {showPriceRequests()}
                      {showTentRequests()}
                      {showDartsRequests()}
                    </>
                  )}
                  {basketEmpty && emptyBasketContent}
                </div>
                <div className={styles.recap}>
                  {!basketEmpty && <div className={styles.line} />}
                  {!basketEmpty && nl && (
                    <Link onClick={checkRequests} href={"/nl/reserveren"}>
                      Reserveren
                    </Link>
                  )}
                  {!basketEmpty && fr && (
                    <Link onClick={checkRequests} href={"/fr/reserver"}>
                      Réserver
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {!cookies && (
          <div className={styles.cartAndHamburger}>
            <div className={styles.basketIcon}>
              <button
                className={styles.shoppingCart}
                aria-label="Shoppingcart basket"
                onClick={() => setMenuOpen((val) => !val)}
                ref={menuCartRef}
              >
                {!basketEmpty && <div className={styles.bolleke}></div>}
                {basketEmpty && <ShoppingCart width={"28px"} height={"28px"} />}
                {!basketEmpty && (
                  <ShoppingCart width={"28px"} height={"28px"} color="#4D7EA8" fill="#4D7EA8" />
                )}
              </button>
            </div>
            <button
              className={menuOpen ? [styles.hamburger, styles.open].join(" ") : styles.hamburger}
              aria-label="hamburger menu"
              ref={menuRef}
              onClick={() => setMenuOpen((val) => !val)}
            >
              <div className={styles.icon}></div>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
